import IndexLayout from '../layouts';
import Wrapper from '../components/Wrapper';
import SiteNav from '../components/header/SiteNav';

import { Button, InputField, Switch, Group } from 'fannypack';
import Spinner from '../../src/components/ui/Spinner';

import { SiteHeader, outer, inner, SiteMain } from '../styles/shared';
import * as React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { PostFullHeader, PostFullTitle, NoImage, PostFull } from '../templates/post';
import { PostFullContent } from '../components/PostContent';
import Footer from '../components/Footer';
import Helmet from 'react-helmet';

//import ShareButtons from '../components/ShareButtons';
import MoreGoodies from '../components/more-goodies/MoreGoodies';
import BackToTop from '../components/BackToTop';

import random from '../utils/random';

const PageTemplate = css`
.site-main {
background #fff;
padding-bottom: 4vw;
}

.post-content {
max-width: 1000px;
margin: -2.5rem auto 0;
}
.post-full-content {
  background: transparent;
}
p {
  font-size: 2rem !important;

}
h2 {
  margin-top: 0;
  font-size: 3rem;

  small {
    font-size: 1.5rem;
    font-weight: normal;
  }
}
h2::before {
all: unset !important;
}
h3 {
text-decoration: none !important;
padding-top: 5rem;
text-align: center;
}

button.active {
  background: #32BA55;
  color: white;
}
`;

const FactsBox = styled.div`
  margin-top: 1.5rem;
  background: #EFFAF2;
  padding: 1.3rem 1.3rem 0.45rem;

  @media (max-width: 750px) {
    padding: 0.1rem 0.1rem 0.1rem;
  }

  li:first-letter {
    color: #ACE3BA;
  }

  h3 {
    padding-top: 0;
  }
`;

const FlexWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  align-items: last baseline;
  margin-bottom: 2rem;

  div {
    margin: 1rem 0.75rem;
  }
`;

const TextureWrapper = styled.div`
  background: white;
  overflow: hidden;
  max-height: 75vh;
  margin-bottom: 2.5rem;

  svg {
    max-width: 100%;
    max-height: 600px;
  }
`;
const ControlsWrapper = styled.div`
  // margin: 0 0 1rem;
`;

interface CloudTextureGeneratorState {
  width: number;
  height: number;
  pendingDownload: boolean;
  abstract: boolean;
  invert: boolean;
  seed: number;
  contrast: boolean;
  contrast2: boolean;
  density: string;
}

class CloudTextureGenerator extends React.Component<undefined, CloudTextureGeneratorState> {
  state = {
    width: 1000,
    height: 600,
    pendingDownload: false,
    abstract: true,
    invert: true,
    seed: 1,
    contrast: true,
    contrast2: true,
    density: 'low',
  };

  canvasRef;
  textureRef;

  constructor(props) {
    super(props);
    this.canvasRef = React.createRef();
    this.textureRef = React.createRef();
  }

  newSeed = () => {
    const seed = random(1, 1000);
    this.setState({ seed });
  };

  handleWidthChange = e => {
    if (!+e.target.value || +e.target.value > 10000) {
      return;
    }

    this.setState({ width: +e.target.value });
  };

  handleHeightChange = e => {
    if (!+e.target.value || +e.target.value > 10000) {
      return;
    }

    this.setState({ height: +e.target.value });
  };

  // help from: http://dinbror.dk/blog/how-to-download-an-inline-svg-as-jpg-or-png/
  // help from: https://javascript.info/blob#blob-to-base64
  generateDownloadable = () => {
    this.setState({ pendingDownload: true });
    const { width } = this.state;

    const canvas = this.canvasRef.current;
    const texture = this.textureRef.current;

    // Sept. 2019: for now it has to be same width/height, bug in Safari when very large and not square
    texture.setAttribute('width', width);
    texture.setAttribute('height', width);

    const xml = new XMLSerializer().serializeToString(texture);
    const svg64 = btoa(xml);
    const b64Start = 'data:image/svg+xml;base64,';
    const image = new Image();
    image.src = b64Start + svg64;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, width);

    image.onload = () => {
      ctx.drawImage(image, 0, 0, width, width);

      canvas.toBlob(
        blob => {
          const link = document.createElement('a');
          link.download = 'cloud-texture.jpg';

          link.href = URL.createObjectURL(blob);
          link.click();
          URL.revokeObjectURL(link.href);

          texture.setAttribute('width', 1000);
          texture.setAttribute('height', 600);
          this.setState({ pendingDownload: false });
        },
        'image/jpeg',
        0.9,
      );
    };
  };

  render() {
    return (
      <IndexLayout>
        <Helmet>
          <title>Cloud Texture Generator for Retouching | Giggster Guide</title>
          <meta
            name="description"
            content="A tool to generate seamless cloud textures and save them as a Jpeg file to then use as a way to add detail and dimension to your photographs."
          />

          <meta property="og:site_name" content="Giggster Guide" />
          <meta property="og:type" content="article" />
          <meta property="og:title" content="Cloud Texture Generator for Retouching" />
          <meta
            property="og:description"
            content="A tool to generate seamless cloud textures and save them as a Jpeg file to then use as a way to add detail and dimension to your photographs."
          />
          <meta property="og:url" content="https://giggster.com/guide/cloud-texture-generator/" />
          <meta
            property="og:image"
            content="https://giggster.com/guide/covers/cloud-texture-generator.jpg"
          />
          <meta property="article:publisher" content="https://www.facebook.com/giggsterHQ" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Cloud Texture Generator for Retouching" />
          <meta
            name="twitter:description"
            content="A tool to generate seamless cloud textures and save them as a Jpeg file to then use as a way to add detail and dimension to your photographs."
          />
          <meta name="twitter:url" content="https://giggster.com/guide/cloud-texture-generator/" />
          <meta
            name="twitter:image"
            content="https://giggster.com/guide/covers/cloud-texture-generator.jpg"
          />
          <meta name="twitter:site" content="@Giggster" />
          <meta name="twitter:creator" content="@Giggster" />
        </Helmet>
        <Wrapper css={PageTemplate}>
          <header css={[outer, SiteHeader]}>
            <div css={inner}>
              <SiteNav />
            </div>
          </header>
          <main id="site-main" className="site-main" css={[SiteMain, outer]}>
            <article className="post page" css={[PostFull, NoImage]}>
              <PostFullHeader>
                <PostFullTitle>Cloud Texture Generator</PostFullTitle>
                <p className="t2">
                  An online tool to generate seamless cloud textures to use with your retouching
                  workflow in Photoshop
                </p>
                {/*<ShareButtons
                  title={'Cloud Texture Generator'}
                  slug={'/cloud-texture-generator/'}
                  imgSrc={'covers/cloud-texture-generator.jpg'}
                />*/}
              </PostFullHeader>

              <PostFullContent className="post-full-content">
                <div className="post-content text-center">
                  <ControlsWrapper>
                    <div>
                      <FlexWrapper>
                        <Switch
                          label="Invert"
                          checked={this.state.invert}
                          // tslint:disable-next-line:react-this-binding-issue
                          onChange={e => this.setState({ invert: e.target.checked })}
                        />
                        <Switch
                          label="More abstract"
                          checked={this.state.abstract}
                          // tslint:disable-next-line:react-this-binding-issue
                          onChange={e => this.setState({ abstract: e.target.checked })}
                        />
                        <Switch
                          label="Contrast 1"
                          checked={this.state.contrast}
                          // tslint:disable-next-line:react-this-binding-issue
                          onChange={e => this.setState({ contrast: e.target.checked })}
                        />
                        <Switch
                          label="Contrast 2"
                          checked={this.state.contrast2}
                          // tslint:disable-next-line:react-this-binding-issue
                          onChange={e => this.setState({ contrast2: e.target.checked })}
                        />
                      </FlexWrapper>
                      <FlexWrapper>
                        Density:
                        <Group>
                          <Button
                            className={`${this.state.density === 'low' ? 'active' : ''}`}
                            size="small"
                            // tslint:disable-next-line:react-this-binding-issue
                            onClick={() => this.setState({ density: 'low' })}
                          >
                            Low
                          </Button>
                          <Button
                            className={`${this.state.density === 'medium' ? 'active' : ''}`}
                            size="small"
                            // tslint:disable-next-line:react-this-binding-issue
                            onClick={() => this.setState({ density: 'medium' })}
                          >
                            Medium
                          </Button>
                          <Button
                            className={`${this.state.density === 'high' ? 'active' : ''}`}
                            size="small"
                            // tslint:disable-next-line:react-this-binding-issue
                            onClick={() => this.setState({ density: 'high' })}
                          >
                            High
                          </Button>
                        </Group>
                      </FlexWrapper>
                    </div>
                  </ControlsWrapper>
                  <TextureWrapper>
                    <h2>Texture Preview</h2>
                    <svg
                      onClick={this.newSeed}
                      style={{ cursor: 'pointer' }}
                      width="1000"
                      height="600"
                      ref={this.textureRef}
                    >
                      <filter id="texture" x="0%" y="0%" width="100%" height="100%">
                        <feTurbulence
                          type={this.state.abstract ? 'fractalNoise' : 'turbulence'}
                          baseFrequency={
                            this.state.density === 'low'
                              ? '0.01'
                              : this.state.density === 'medium'
                              ? '0.015'
                              : '0.02'
                          }
                          numOctaves="4"
                          seed={this.state.seed}
                          result="NOISE"
                          stitchTiles="stitch"
                        />
                        <feColorMatrix
                          type="matrix"
                          values="0.2126 0.7152 0.0722 0 0 0.2126 0.7152 0.0722 0 0 0.2126 0.7152 0.0722 0 0 0 0 0 1 0"
                          in="NOISE"
                          result="BNW"
                        />

                        {this.state.contrast && (
                          <feComponentTransfer result="componentTransfer1">
                            <feFuncR type="table" tableValues="0 0 0 0 0.2 0.4 0.6 0.8 1" />
                            <feFuncG type="table" tableValues="0 0 0 0 0.2 0.4 0.6 0.8 1" />
                            <feFuncB type="table" tableValues="0 0 0 0 0.2 0.4 0.6 0.8 1" />
                            <feFuncA type="table" tableValues="1 1 1 1 1 1 1 1 1" />
                          </feComponentTransfer>
                        )}

                        <feComponentTransfer
                          in={this.state.contrast ? 'componentTransfer1' : 'BNW'}
                          result="GRADIENTMAP"
                        >
                          <feFuncR type="table" tableValues="1 0" />
                          <feFuncG type="table" tableValues="1 0" />
                          <feFuncB type="table" tableValues="1 0" />
                          <feFuncA type="table" tableValues="0 1" />
                        </feComponentTransfer>

                        {this.state.invert && (
                          <feColorMatrix
                            in="GRADIENTMAP"
                            result="INVERTED"
                            type="matrix"
                            values="-1 0 0 0 1
                                    0 -1 0 0 1
                                    0 0 -1 0 1
                                    0 0 0 1 0"
                          />
                        )}

                        {this.state.contrast2 && (
                          <>
                            <feFlood floodColor="black" result="BLACK" />
                            <feBlend
                              mode="soft-light"
                              in="BLACK"
                              in2={this.state.invert ? 'INVERTED' : 'GRADIENTMAP'}
                            />
                          </>
                        )}
                      </filter>

                      <rect
                        x="0"
                        y="0"
                        width="100%"
                        height="100%"
                        filter={`url(#texture)`}
                        fill="none"
                      />
                    </svg>
                  </TextureWrapper>
                  <FlexWrapper>
                    <InputField
                      a11yId="long-edge"
                      // size="medium"
                      label="Size of long edge (in px)"
                      type="number"
                      value={`${this.state.width.toString()}`}
                      onChange={this.handleWidthChange}
                    />
                    {/* <InputField
                      a11yId="height"
                      // size="medium"
                      label="Height (in px)"
                      type="number"
                      value={`${this.state.height.toString()}`}
                      onChange={this.handleHeightChange}
                    /> */}
                    {this.state.pendingDownload ? (
                      <Spinner />
                    ) : (
                      <Button palette="primary" onClick={this.generateDownloadable}>
                        Download Texture
                      </Button>
                    )}
                  </FlexWrapper>

                  <hr />

                  <FactsBox>
                    <h3>How it works</h3>
                    <ul>
                      <li>
                        &#9673; You can play with the settings to get different effects for the
                        final texture. Click on the texture preview to get another random variation
                        using the selected settings.
                      </li>
                      <li>
                        &#9673; You can choose the size in pixels for the long edge. You can either
                        choose for it to cover your entire image size, or you can export a smaller
                        texture and tile it since these textures are seamless. The maximum size
                        currently is 10,000 pixels.
                      </li>
                      <li>
                        &#9673; Cloud textures can be useful to add abstract texture to backgrounds
                        in your photos. Just add them as a new layer, set the blend mode of the
                        cloud texture layer to something like <em>Screen</em>, and then reduce the
                        opacity and mask out where you don't want the texture to apply. Don't
                        hesitate to also tweak the texture in Photoshop by blurring it up.
                      </li>
                      <li>
                        &#9673; These cloud textures are not meant to look exactly like clouds in
                        the sky look like, more like abstract fluffiness to add this extra touch of
                        detail to your photographs.
                      </li>
                    </ul>
                  </FactsBox>
                </div>
              </PostFullContent>
            </article>
            <MoreGoodies slug="cloud-texture-generator" />
          </main>
          <BackToTop />
          <Footer />
        </Wrapper>
        <canvas
          ref={this.canvasRef}
          width={this.state.width}
          height={this.state.width}
          style={{
            width: `${this.state.width}px`,
            height: `${this.state.width}px`,
            maxWidth: 'none',
            maxHeight: 'none',
            display: 'none',
          }}
        />
      </IndexLayout>
    );
  }
}

export default CloudTextureGenerator;
